import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractInputComponent } from '../abstract-input.component';
import {
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyInputComponent } from '../empty-input/empty-input.component';

@Component({
  selector: 'common-currency-input',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    EmptyInputComponent,
    FormsModule,
  ],
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
})
export class CurrencyInputComponent
  extends AbstractInputComponent
  implements OnInit
{
  @Input()
  public max: number;

  @Input()
  public min: number;

  @Output()
  public itemChange: EventEmitter<number> = new EventEmitter<number>();

  public formattedValue: string;

  protected override createValidators(): ValidatorFn[] {
    const validators = super.createValidators();
    if (this.max !== undefined) {
      validators.push(Validators.max(this.max));
    }
    if (this.min !== undefined) {
      validators.push(Validators.min(this.min));
    }
    return validators;
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    if (this.property) {
      this.formattedValue = this.formatCurrency(this.item[this.property]);
    } else {
      this.formattedValue = this.formatCurrency(this.item);
    }
  }

  // This method handles the input event and formats the value
  public onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let stringValue = inputElement.value;

    // Remove all non-digit characters except for the first dot for decimals
    // This regex allows only digits and a single decimal point
    stringValue = stringValue.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = stringValue.split('.');
    if (parts.length > 2) {
      stringValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal part to 2 digits
    if (parts[1] && parts[1].length > 2) {
      stringValue = parts[0] + '.' + parts[1].substring(0, 2);
    }

    this.formattedValue = this.formatCurrency(stringValue);
    if (this.property) {
      this.item[this.property] = +stringValue;
    } else {
      this.item = +stringValue;
    }
    if (this.control) {
      this.control.setValue(+stringValue, { emitEvent: false });
    }
    this.itemChange.emit(this.item);
  }

  // Helper function to format the number as currency
  private formatCurrency(value: string | number): string {
    if (!value) {
      return ''; // Return empty string if value is empty
    }

    value = value.toString();

    // Remove existing commas and dollar signs for clean processing
    let numericValue = value.replace(/,/g, '').replace('$', '');

    // Handle cases where the input might be just a decimal point or starts with one
    if (numericValue === '.') {
      return '$ 0.';
    }
    if (numericValue.startsWith('.')) {
      numericValue = '0' + numericValue;
    }

    // Split into integer and decimal parts
    const parts = numericValue.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';

    // Add thousand separators to the integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Reconstruct the value with dollar sign
    let formatted = `$ ${integerPart}`;
    if (parts.length > 1) {
      formatted += `.${decimalPart}`;
    }

    return formatted;
  }
}
